import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, BookOpen, Camera, CarFront, Check, FileText, Lock, LogOut, MessageCircle,
  Phone, RotateCcw, Scissors, Settings2, Trash2, TriangleAlert, Truck, X,
} from "lucide-react";
import { EASE, MARK, gbp } from "@/components/common";
import { WHATSAPP_URL } from "@/data/vehicles";
import { apiFetch, fetchFileBlobUrl } from "@/api";
import AgreementModal from "@/components/AgreementModal";

const STATUSES = ["Pending", "Approved", "Active", "Completed", "Rejected"];

const STATUS_STYLE = {
  Pending: "text-amber-200 border-amber-200/30",
  Approved: "text-emerald-300 border-emerald-300/30",
  Active: "text-sky-300 border-sky-300/30",
  Completed: "text-zinc-300 border-zinc-400/30",
  Rejected: "text-red-300 border-red-300/30",
};

const RECOVERY_PAYMENT_LABELS = {
  awaiting_payment: "Awaiting Payment",
  deposit_paid: "Deposit Paid",
  balance_authorised: "Balance Authorised",
  fully_paid: "Fully Paid",
  released: "Released / Refunded",
};

const RECOVERY_PAYMENT_STYLE = {
  awaiting_payment: "text-amber-200 border-amber-200/30",
  deposit_paid: "text-sky-300 border-sky-300/30",
  balance_authorised: "text-emerald-300 border-emerald-300/30",
  fully_paid: "text-emerald-300 border-emerald-300/30",
  released: "text-zinc-400 border-zinc-500/30",
};

const RECOVERY_STATUS_STYLE = {
  Pending: "text-amber-200 border-amber-200/30",
  Accepted: "text-emerald-300 border-emerald-300/30",
  "In Progress": "text-sky-300 border-sky-300/30",
  Completed: "text-zinc-300 border-zinc-400/30",
  Unavailable: "text-red-300 border-red-300/30",
};

const LOGISTICS_LABELS = {
  peterborough: "Collection from Peterborough",
  delivery: "Delivery Only",
  collection: "Collection Only",
  both: "Delivery + Collection",
};

const PAYMENT_LABELS = {
  not_configured: "Payment to be confirmed",
  awaiting_card: "Awaiting card authorisation",
  authorised: "Authorised — pending capture",
  captured: "Captured",
  authorisation_released: "Authorisation released",
  payment_error: "Payment error",
  deposit_refunded_full: "Deposit refunded in full",
  deposit_refunded_partial: "Deposit partially refunded",
};

const METHOD_LABELS = { card: "Card", klarna: "Klarna", afterpay_clearpay: "Clearpay" };

const INSPECTION_GROUPS = [
  { group: "Exterior", items: ["Bodywork", "Paintwork", "Windscreen", "Mirrors", "Lights", "Tyres", "Alloys"] },
  { group: "Interior", items: ["Seats", "Dashboard", "Warning Lights", "Air Conditioning / Heating", "Infotainment"] },
  { group: "Mechanical & Handover", items: ["Mechanical Checks", "Mileage", "Fuel Level", "Accessories"] },
];

const telLink = (phone) => `tel:+44${String(phone || "").replace(/\D/g, "").replace(/^0/, "")}`;
const waLinkAdmin = (phone) => `https://wa.me/44${String(phone || "").replace(/\D/g, "").replace(/^0/, "")}`;

const SafetyRow = ({ label, value, danger }) => (
  <div className="flex items-start justify-between gap-6 py-2.5 border-b border-white/[0.06]">
    <span className="text-[0.62rem] tracking-[0.22em] uppercase text-zinc-500 pt-0.5">{label}</span>
    <span className={`text-sm font-light text-right flex items-center gap-2 ${danger ? "text-red-300" : "text-zinc-200"}`}>
      {danger && <TriangleAlert size={13} />} {value || "—"}
    </span>
  </div>
);

const Row = ({ label, value, testid }) => (
  <div className="flex items-start justify-between gap-6 py-2.5 border-b border-white/[0.06]">
    <span className="text-[0.62rem] tracking-[0.22em] uppercase text-zinc-500 pt-0.5">{label}</span>
    <span className="text-sm text-zinc-200 font-light text-right" data-testid={testid}>{value}</span>
  </div>
);

const SectionHead = ({ children }) => (
  <p className="text-[0.62rem] tracking-[0.32em] uppercase text-white mt-8 mb-3">{children}</p>
);

export default function Admin() {
  const [auth, setAuth] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    apiFetch("/auth/me")
      .then((u) => { setUser(u); setAuth(true); })
      .catch(() => setAuth(false));
  }, []);

  if (auth === null) return <div className="bg-[#050506] min-h-screen" />;
  if (auth === false) return <Login onSuccess={(u) => { setUser(u); setAuth(true); }} />;
  return <Dashboard user={user} onLogout={() => { localStorage.removeItem("spt_admin_token"); setAuth(false); setUser(null); }} />;
}

const Login = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await apiFetch("/auth/login", { method: "POST", body: { email, password } });
      localStorage.setItem("spt_admin_token", res.token);
      onSuccess(res.user);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-[#050506] text-zinc-100 min-h-screen flex items-center justify-center px-6">
      <div className="vignette-overlay" />
      <motion.div className="relative z-10 w-full max-w-sm" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
        <div className="text-center mb-10">
          <img src={MARK} alt="Senna Prestige Travels" className="h-14 w-auto mx-auto mb-6" />
          <p className="text-[0.62rem] tracking-[0.4em] uppercase text-zinc-400">Owner Access</p>
        </div>
        <div className="space-y-5">
          <label className="block">
            <span className="field-label">Email</span>
            <input data-testid="admin-login-email" type="email" className="field" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          </label>
          <label className="block">
            <span className="field-label">Password</span>
            <input data-testid="admin-login-password" type="password" className="field" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} autoComplete="current-password" />
          </label>
          {error && <p data-testid="admin-login-error" className="text-xs text-red-300 font-light">{error}</p>}
          <button data-testid="admin-login-submit" onClick={submit} disabled={busy} className="btn-primary w-full justify-center disabled:opacity-50">
            <Lock size={13} /> {busy ? "Signing In…" : "Sign In"}
          </button>
          <Link to="/" className="block text-center text-[0.6rem] tracking-[0.3em] uppercase text-zinc-500 hover:text-white transition-colors">Back to Site</Link>
        </div>
      </motion.div>
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [section, setSection] = useState("rentals");
  const [rentalTab, setRentalTab] = useState("bookings");

  const logout = async () => {
    try { await apiFetch("/auth/logout", { method: "POST" }); } catch {}
    onLogout();
  };

  const go = (s, t) => { setSection(s); if (t) setRentalTab(t); window.scrollTo(0, 0); };

  const desktopTabs = [
    ["Rental Bookings", section === "rentals" && rentalTab === "bookings", () => go("rentals", "bookings")],
    ["Availability", section === "rentals" && rentalTab === "availability", () => go("rentals", "availability")],
    ["Vehicle Enquiries", section === "rentals" && rentalTab === "enquiries", () => go("rentals", "enquiries")],
    ["Recovery Bookings", section === "recoveries", () => go("recoveries")],
    ["System", section === "account", () => go("account")],
  ];

  return (
    <div className="bg-[#050506] text-zinc-100 min-h-screen">
      <header className="border-b border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" data-testid="admin-home-link" className="flex items-center gap-3">
            <img src={MARK} alt="Senna Prestige Travels" className="h-8 w-auto" />
            <span className="hidden sm:block text-[0.6rem] tracking-[0.42em] uppercase text-zinc-300">Admin</span>
          </Link>
          <div className="flex items-center gap-5">
            <span className="hidden md:block text-[0.6rem] tracking-[0.2em] uppercase text-zinc-500">{user?.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="nav-link text-[0.62rem] tracking-[0.3em] uppercase text-zinc-400 flex items-center gap-2">
              <LogOut size={13} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="admin-main max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-16">
        <div className="hidden md:flex flex-wrap gap-2 mb-10" data-testid="admin-main-tabs">
          {desktopTabs.map(([label, active, fn]) => (
            <button
              key={label}
              data-testid={`admin-maintab-${label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={fn}
              className={`px-5 py-2.5 text-[0.6rem] tracking-[0.25em] uppercase transition-all duration-300 ${
                active ? "bg-white text-black" : "text-zinc-400 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)] hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="md:hidden mb-7">
          <p className="kicker" data-testid="admin-mobile-section-title">
            {section === "rentals" ? "Rentals" : section === "recoveries" ? "Recoveries" : "Account"}
          </p>
          {section === "rentals" && (
            <div className="mt-4 flex shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]" data-testid="admin-rentals-seg">
              {[["bookings", "Rental Bookings"], ["availability", "Availability"], ["enquiries", "Enquiries"]].map(([id, label]) => (
                <button key={id} data-testid={`admin-seg-${id}`} onClick={() => go("rentals", id)} className={`seg-btn ${rentalTab === id ? "active" : ""}`}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {section === "rentals" && rentalTab === "bookings" && <BookingsPanel />}
        {section === "rentals" && rentalTab === "availability" && <AvailabilityPanel />}
        {section === "rentals" && rentalTab === "enquiries" && <EnquiriesPanel type="vehicle" />}
        {section === "recoveries" && <RecoveryBookingsPanel />}
        {section === "account" && <SystemPanel />}
      </main>

      <nav data-testid="admin-bottom-nav" className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#050506]/95 backdrop-blur-md border-t border-white/10 flex">
        <button data-testid="admin-nav-rentals" onClick={() => go("rentals", "bookings")} className={`bottom-nav-btn ${section === "rentals" ? "active" : ""}`}>
          <CarFront size={19} strokeWidth={1.5} /> Rentals
        </button>
        <button data-testid="admin-nav-recoveries" onClick={() => go("recoveries")} className={`bottom-nav-btn ${section === "recoveries" ? "active" : ""}`}>
          <Truck size={19} strokeWidth={1.5} /> Recoveries
        </button>
        <button data-testid="admin-nav-account" onClick={() => go("account")} className={`bottom-nav-btn ${section === "account" ? "active" : ""}`}>
          <Settings2 size={19} strokeWidth={1.5} /> Account
        </button>
      </nav>
    </div>
  );
};

/* ---------------- RENTAL BOOKINGS ---------------- */

const BookingsPanel = () => {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== "All") params.set("status", filter);
      if (search.trim()) params.set("q", search.trim());
      const data = await apiFetch(`/admin/bookings?${params.toString()}`);
      setBookings(data);
      if (selected) {
        const fresh = data.find((b) => b.ref === selected.ref);
        if (fresh) setSelected(fresh);
      }
    } catch (e) {
      setError(e.message);
    }
  }, [filter, search, selected]);

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const openBooking = async (ref) => {
    try {
      setSelected(await apiFetch(`/admin/bookings/${ref}`));
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-8">
        <div className="flex flex-wrap gap-2 w-full md:w-auto" data-testid="admin-tabs">
          {["All", ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`admin-tab-${s.toLowerCase()}`}
              onClick={() => setFilter(s)}
              className={`px-4 py-2.5 md:py-2 text-[0.58rem] tracking-[0.22em] uppercase transition-all duration-300 ${
                filter === s ? "bg-white text-black" : "text-zinc-400 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)] hover:text-white"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <input
          data-testid="admin-search"
          className="field w-full md:!w-64 md:ml-auto"
          placeholder="Search ref, customer, vehicle…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
        />
        <button data-testid="admin-search-btn" className="admin-action md:!w-auto" onClick={load}>Search</button>
      </div>
      {error && <p className="text-xs text-red-300 mb-4">{error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-10 items-start">
        <div>
          <div className="hidden md:block divide-y divide-white/[0.06] border-y border-white/[0.08]">
            {bookings.map((b) => (
              <button
                key={b.ref}
                data-testid={`admin-row-${b.ref}`}
                onClick={() => openBooking(b.ref)}
                className={`admin-table-row w-full text-left px-5 py-5 flex items-center justify-between gap-4 ${selected?.ref === b.ref ? "selected" : ""}`}
              >
                <div>
                  <p className="text-sm text-white font-light">{b.customer.name}</p>
                  <p className="mt-1.5 text-[0.62rem] tracking-[0.2em] uppercase text-zinc-500">{b.ref} · {b.vehicle_name} · {b.package_label}</p>
                </div>
                <span className={`status-pill border ${STATUS_STYLE[b.booking_status]}`}>{b.booking_status}</span>
              </button>
            ))}
            {bookings.length === 0 && <p className="px-5 py-10 text-sm text-zinc-500 font-light">No bookings found.</p>}
          </div>

          <div className="md:hidden space-y-4" data-testid="admin-mobile-cards">
            {bookings.map((b) => (
              <button
                key={b.ref}
                data-testid={`admin-card-${b.ref}`}
                onClick={() => openBooking(b.ref)}
                className="m-card w-full text-left p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="font-display text-lg text-white leading-snug">{b.customer.name}</p>
                  <span className={`status-pill border shrink-0 ${STATUS_STYLE[b.booking_status]}`}>{b.booking_status}</span>
                </div>
                <p className="mt-3 text-sm text-zinc-300 font-light">{b.vehicle_name}</p>
                <p className="mt-1 text-sm text-zinc-500 font-light">{b.start_date} → {b.end_date}</p>
                <p className="mt-3 text-[0.6rem] tracking-[0.25em] uppercase text-zinc-600">{b.ref}</p>
              </button>
            ))}
            {bookings.length === 0 && <p className="py-10 text-center text-sm text-zinc-500 font-light">No bookings found.</p>}
          </div>
        </div>

        {selected && (
          <div className="max-md:fixed max-md:inset-0 max-md:z-50 max-md:overflow-y-auto max-md:bg-[#050506] max-md:px-4 max-md:py-6">
            <button data-testid="admin-detail-close" onClick={() => setSelected(null)} className="md:hidden mb-5 flex items-center gap-2 text-[0.62rem] tracking-[0.3em] uppercase text-zinc-400">
              <ArrowLeft size={14} /> All Bookings
            </button>
            <BookingDetail key={selected.ref + (selected.history?.length || 0)} booking={selected} refresh={() => openBooking(selected.ref)} />
          </div>
        )}
        {!selected && (
          <div className="hidden lg:flex items-center justify-center min-h-[300px] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)] text-zinc-600 text-sm font-light">
            Select a booking to review it.
          </div>
        )}
      </div>
    </div>
  );
};

const BookingDetail = ({ booking, refresh }) => {
  const [detailTab, setDetailTab] = useState("details");
  const [agreementOpen, setAgreementOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const act = async (path, body) => {
    setError(""); setNotice("");
    try {
      await apiFetch(path, { method: "POST", body: body || {} });
      await refresh();
      setNotice("Done.");
    } catch (e) {
      setError(e.message);
    }
  };

  const viewDoc = async (kind) => {
    try {
      const url = await fetchFileBlobUrl(`/admin/bookings/${booking.ref}/documents/${kind}`);
      window.open(url, "_blank");
    } catch {
      setError("Document not available yet.");
    }
  };

  return (
    <div className="shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] bg-[#08080a] p-5 md:p-9" data-testid="admin-detail-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-[0.6rem] tracking-[0.3em] uppercase text-zinc-500">{booking.ref} · created {new Date(booking.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
          <h2 className="font-display text-2xl text-white mt-2">{booking.vehicle_name}</h2>
        </div>
        <span className={`status-pill border ${STATUS_STYLE[booking.booking_status]}`} data-testid="admin-detail-status">{booking.booking_status}</span>
      </div>

      <div className="mt-6 flex gap-2">
        {["details", "inspection"].map((t) => (
          <button
            key={t}
            data-testid={`admin-${t}-tab`}
            onClick={() => setDetailTab(t)}
            className={`flex-1 md:flex-none px-4 py-3 md:py-2 text-[0.58rem] tracking-[0.25em] uppercase transition-all duration-300 ${
              detailTab === t ? "bg-white/[0.08] text-white shadow-[inset_0_0_0_1px_rgba(255,255,255,0.25)]" : "text-zinc-500"
            }`}
          >
            {t === "details" ? "Details" : "Inspection"}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={detailTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35, ease: EASE }}>
          {detailTab === "details" ? (
            <Details booking={booking} act={act} viewDoc={viewDoc} setAgreementOpen={setAgreementOpen} refresh={refresh} setError={setError} setNotice={setNotice} />
          ) : (
            <Inspection booking={booking} refresh={refresh} setError={setError} setNotice={setNotice} />
          )}
        </motion.div>
      </AnimatePresence>

      {notice && <p data-testid="admin-notice" className="mt-6 text-xs text-emerald-300/90 font-light tracking-wide">{notice}</p>}
      {error && <p data-testid="admin-error" className="mt-6 text-xs text-red-300 font-light tracking-wide">{error}</p>}

      <AgreementModal open={agreementOpen} onClose={() => setAgreementOpen(false)} />
    </div>
  );
};

const Details = ({ booking, act, viewDoc, setAgreementOpen, refresh, setError, setNotice }) => {
  const [noteText, setNoteText] = useState("");
  const [refundMode, setRefundMode] = useState(null);
  const [deduction, setDeduction] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [busy, setBusy] = useState(false);

  const ded = parseFloat(deduction) || 0;
  const refunded = booking.deposit_refunded || 0;
  const remaining = Math.max(0, booking.deposit - refunded);
  const toReturn = Math.max(0, remaining - ded);

  const doTransition = (action) => act(`/admin/bookings/${booking.ref}/transition`, { action });

  const doRefund = async (mode) => {
    setBusy(true);
    setError(""); setNotice("");
    try {
      const body = mode === "full" ? { mode: "full", reason: refundReason } : { mode: "partial", amount: toReturn, reason: refundReason };
      await apiFetch(`/admin/bookings/${booking.ref}/refund`, { method: "POST", body });
      setRefundMode(null); setDeduction(""); setRefundReason("");
      await refresh();
      setNotice("Refund recorded.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await apiFetch(`/admin/bookings/${booking.ref}/notes`, { method: "POST", body: { text: noteText } });
      setNoteText("");
      await refresh();
    } catch (e) {
      setError(e.message);
    }
  };

  const docs = booking.documents || {};

  return (
    <div>
      <SectionHead>Customer</SectionHead>
      <Row label="Name" value={booking.customer.name} />
      <Row label="Date of Birth" value={booking.customer.dob} />
      <Row label="Address" value={`${booking.customer.address} · ${booking.customer.postcode}`} />
      <Row label="Contact" value={`${booking.customer.phone} · ${booking.customer.email}`} />
      <Row label="NI Number" value={booking.ni_provided ? "Provided" : "Not provided (optional)"} />

      <SectionHead>Hire</SectionHead>
      <Row label="Vehicle" value={booking.vehicle_name} />
      <Row label="Package" value={booking.package_label} />
      <Row label="Dates" value={`${booking.start_date} → ${booking.end_date}`} />
      <Row label="Logistics" value={LOGISTICS_LABELS[booking.logistics] || booking.logistics} />
      {booking.delivery_postcode && <Row label="Delivery To" value={`${booking.delivery_postcode}${booking.delivery_miles ? ` · ${booking.delivery_miles} miles` : ""}`} />}
      {booking.collection_postcode && <Row label="Collection From" value={`${booking.collection_postcode}${booking.collection_miles ? ` · ${booking.collection_miles} miles` : ""}`} />}

      <SectionHead>Payment Breakdown</SectionHead>
      <Row label="Hire Amount" value={gbp(booking.hire_charge)} />
      {booking.delivery_charge > 0 && <Row label="Delivery Charge" value={gbp(booking.delivery_charge)} />}
      {booking.collection_charge > 0 && <Row label="Collection Charge" value={gbp(booking.collection_charge)} />}
      <Row label="Security Deposit" value={`${gbp(booking.deposit)}${refunded > 0 ? ` · refunded ${gbp(refunded)}` : ""}`} />
      <Row label="Total" value={gbp(booking.total)} testid="admin-detail-total" />
      <Row label="Payment Status" value={PAYMENT_LABELS[booking.payment_status] || booking.payment_status} testid="admin-payment-status" />
      {booking.payment_method && <Row label="Payment Method" value={METHOD_LABELS[booking.payment_method] || booking.payment_method} testid="admin-payment-method" />}

      <SectionHead>Documents & Agreement</SectionHead>
      <div className="flex flex-wrap gap-3 mt-1">
        {[["licence_front", "Licence — Front"], ["licence_back", "Licence — Back"], ["proof", "Proof of Address"]].map(([kind, label]) => (
          <button
            key={kind}
            data-testid={`admin-doc-${kind}`}
            onClick={() => viewDoc(kind)}
            disabled={!docs[kind]}
            className={`admin-action md:!w-auto ${!docs[kind] ? "opacity-40 cursor-default" : ""}`}
          >
            <FileText size={13} /> {label} {docs[kind] ? "" : "— pending"}
          </button>
        ))}
      </div>
      <div className="mt-4">
        <Row label="Document Status" value={booking.doc_status} />
        <Row label="Rental Agreement" value={booking.agreement?.accepted ? `Accepted · ${booking.agreement.version} · ${new Date(booking.agreement.accepted_at).toLocaleString("en-GB")}` : "Not accepted"} />
      </div>
      <button data-testid="admin-view-agreement" onClick={() => setAgreementOpen(true)} className="mt-4 link-arrow">
        <BookOpen size={13} /> View Agreement & Repair Cost Schedule
      </button>

      {booking.refunds?.length > 0 && (
        <>
          <SectionHead>Refund History</SectionHead>
          {booking.refunds.map((r) => (
            <Row key={r.id} label={`${r.mode} · ${new Date(r.at).toLocaleDateString("en-GB")}`} value={`${gbp(r.amount)} · ${r.status}${r.reason ? ` · ${r.reason}` : ""}`} />
          ))}
        </>
      )}

      <SectionHead>Status History</SectionHead>
      <div className="space-y-2 max-h-44 overflow-y-auto pr-2">
        {[...(booking.history || [])].reverse().map((h, i) => (
          <p key={i} className="text-xs text-zinc-500 font-light">
            <span className="text-zinc-300">{h.action}</span> · {new Date(h.at).toLocaleString("en-GB")} · {h.by}
          </p>
        ))}
      </div>

      <SectionHead>Internal Notes</SectionHead>
      <div className="space-y-2 mb-4">
        {(booking.admin_notes || []).map((n, i) => (
          <p key={i} className="text-sm text-zinc-300 font-light border-l border-white/15 pl-3">{n.text} <span className="text-zinc-600 text-xs">— {n.by}, {new Date(n.at).toLocaleDateString("en-GB")}</span></p>
        ))}
        {(booking.admin_notes || []).length === 0 && <p className="text-xs text-zinc-600 font-light">No notes yet.</p>}
      </div>
      <div className="flex gap-3">
        <input data-testid="admin-note-input" className="field" placeholder="Add internal note…" value={noteText} onChange={(e) => setNoteText(e.target.value)} />
        <button data-testid="admin-note-add" className="admin-action md:!w-auto shrink-0" onClick={addNote}>Add</button>
      </div>

      <SectionHead>Actions</SectionHead>
      <div className="flex flex-wrap gap-3 mt-2" data-testid="admin-actions">
        {booking.booking_status === "Pending" && (
          <>
            <button data-testid="admin-approve-btn" className="admin-action primary" onClick={() => doTransition("approve")}>
              <Check size={13} /> Approve Booking
            </button>
            <button data-testid="admin-reject-btn" className="admin-action danger" onClick={() => doTransition("reject")}>
              <X size={13} /> Reject Booking
            </button>
          </>
        )}
        {booking.booking_status === "Approved" && (
          <button data-testid="admin-activate-btn" className="admin-action" onClick={() => doTransition("activate")}>Mark Active</button>
        )}
        {(booking.booking_status === "Approved" || booking.booking_status === "Active") && (
          <button data-testid="admin-complete-btn" className="admin-action" onClick={() => doTransition("complete")}>
            <Check size={13} /> Complete Booking
          </button>
        )}
        <a data-testid="admin-contact-btn" className="admin-action" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
          <MessageCircle size={13} /> Contact Customer
        </a>
      </div>

      {remaining > 0 && ["Approved", "Active", "Completed"].includes(booking.booking_status) && (
        <div className="mt-8 p-5 md:p-6 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] bg-white/[0.02]">
          <p className="text-[0.62rem] tracking-[0.3em] uppercase text-white mb-4">Security Deposit — {gbp(remaining)} remaining</p>
          <div className="flex flex-wrap gap-3">
            <button data-testid="admin-full-refund-btn" className="admin-action md:!w-auto" disabled={busy} onClick={() => doRefund("full")}>
              <RotateCcw size={13} /> Full Deposit Refund
            </button>
            <button data-testid="admin-partial-refund-btn" className="admin-action md:!w-auto" onClick={() => setRefundMode(refundMode === "partial" ? null : "partial")}>
              <Scissors size={13} /> Partial Deposit Refund
            </button>
          </div>
          {refundMode === "partial" && (
            <div className="mt-6 max-w-sm">
              <label className="block">
                <span className="field-label">Approved Deduction (£)</span>
                <input data-testid="admin-deduction-input" type="number" min="0" className="field" placeholder="e.g. 180" value={deduction} onChange={(e) => setDeduction(e.target.value)} />
              </label>
              <label className="block mt-4">
                <span className="field-label">Reason (internal)</span>
                <input data-testid="admin-refund-reason" className="field" placeholder="e.g. Alloy scuff — per repair schedule" value={refundReason} onChange={(e) => setRefundReason(e.target.value)} />
              </label>
              <div className="mt-5 space-y-2 text-sm font-light">
                <p className="flex justify-between text-zinc-400"><span>Remaining deposit</span><span className="text-white">{gbp(remaining)}</span></p>
                <p className="flex justify-between text-zinc-400"><span>Minus deduction</span><span className="text-amber-200">− {gbp(ded)}</span></p>
                <p className="flex justify-between text-zinc-400 border-t border-white/10 pt-2"><span>Amount to return</span><span className="text-white" data-testid="admin-refund-amount">{gbp(toReturn)}</span></p>
              </div>
              <p className="mt-4 text-[0.62rem] tracking-[0.12em] text-zinc-600 font-light leading-relaxed">
                Deductions are manual owner decisions assessed per the Repair Cost Schedule — never auto-charged. Where Stripe payment is active, the refund is issued through Stripe and the reference is stored.
              </p>
              <button data-testid="admin-confirm-partial-refund" className="admin-action primary mt-5" disabled={busy || toReturn <= 0} onClick={() => doRefund("partial")}>
                <Check size={13} /> {busy ? "Processing…" : "Refund " + gbp(toReturn)}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------------- INSPECTION ---------------- */

const Inspection = ({ booking, refresh, setError, setNotice }) => {
  const existing = booking.inspection || {};
  const [checks, setChecks] = useState(existing.checks || {});
  const [damage, setDamage] = useState(existing.damage || { found: false, description: "", est_cost: "" });
  const [notes, setNotes] = useState(existing.notes || "");
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState(existing.photos || {});

  const toggle = (key, side) => setChecks((c) => ({ ...c, [`${key}-${side}`]: !c[`${key}-${side}`] }));

  const save = async () => {
    setBusy(true);
    try {
      await apiFetch(`/admin/bookings/${booking.ref}/inspection`, { method: "PUT", body: { checks, damage, notes } });
      await refresh();
      setNotice("Inspection record saved.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (side, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      await apiFetch(`/admin/bookings/${booking.ref}/inspection/photos?side=${side}`, { method: "POST", formData: fd });
      await refresh();
      setPhotos((await apiFetch(`/admin/bookings/${booking.ref}`)).inspection?.photos || {});
    } catch (e) {
      setError(e.message);
    }
  };

  const viewPhoto = async (side, idx) => {
    const url = await fetchFileBlobUrl(`/admin/bookings/${booking.ref}/inspection-photo/${idx}?side=${side}`);
    window.open(url, "_blank");
  };

  return (
    <div>
      <p className="mt-4 text-sm text-zinc-400 font-light leading-relaxed">
        Condition checklist based on the rental agreement — completed before delivery and after return. Photos are stored privately with the booking.
      </p>

      <SectionHead>Photographic Evidence</SectionHead>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[["before", "Before-Delivery Photos"], ["after", "After-Return Photos"]].map(([side, label]) => (
          <div key={side} className="file-drop" data-testid={`admin-photos-${side}`}>
            <input type="file" id={`admin-photos-${side}-input`} className="hidden" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => uploadPhoto(side, e.target.files?.[0])} />
            <label htmlFor={`admin-photos-${side}-input`} className="flex items-center justify-between gap-4 cursor-pointer px-5 py-4">
              <span className="text-sm font-light flex items-center gap-3 text-zinc-300">
                <Camera size={14} className="text-zinc-500" /> {label} ({(photos[side] || []).length})
              </span>
              <span className="text-[0.55rem] tracking-[0.25em] uppercase text-zinc-500">Upload</span>
            </label>
            {(photos[side] || []).length > 0 && (
              <div className="px-5 pb-4 flex flex-wrap gap-2">
                {photos[side].map((p, i) => (
                  <button key={i} onClick={() => viewPhoto(side, i)} className="text-[0.58rem] tracking-[0.2em] uppercase text-zinc-400 underline underline-offset-4 hover:text-white">
                    {p.filename || `Photo ${i + 1}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {INSPECTION_GROUPS.map((g) => (
        <div key={g.group}>
          <SectionHead>{g.group}</SectionHead>
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 items-center border-b border-white/[0.08] pb-2 mb-1">
            <span />
            <span className="text-[0.52rem] tracking-[0.25em] uppercase text-zinc-600 w-14 text-center">Before</span>
            <span className="text-[0.52rem] tracking-[0.25em] uppercase text-zinc-600 w-14 text-center">After</span>
          </div>
          {g.items.map((item) => {
            const key = `${g.group}-${item}`;
            return (
              <div key={key} className="grid grid-cols-[1fr_auto_auto] gap-x-6 items-center py-3 md:py-2.5 border-b border-white/[0.05]">
                <span className="text-sm text-zinc-300 font-light">{item}</span>
                {["before", "after"].map((side) => (
                  <button
                    key={side}
                    data-testid={`admin-check-${key}-${side}`.toLowerCase().replace(/[\s/]+/g, "-")}
                    onClick={() => toggle(key, side)}
                    className={`chk w-14 !h-8 md:!h-[18px] justify-self-center ${checks[`${key}-${side}`] ? "on" : ""}`}
                    aria-label={`${item} ${side}`}
                  >
                    {checks[`${key}-${side}`] && <Check size={11} />}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
      ))}

      <SectionHead>Damage Summary</SectionHead>
      <div className="flex gap-3 mb-4">
        {[[false, "No New Damage"], [true, "New Damage Found"]].map(([val, label]) => (
          <button
            key={label}
            data-testid={`admin-damage-${val ? "yes" : "no"}`}
            onClick={() => setDamage((d) => ({ ...d, found: val }))}
            className={`flex-1 md:flex-none px-5 py-3 md:py-2.5 text-[0.6rem] tracking-[0.25em] uppercase transition-all duration-300 ${
              damage.found === val ? "bg-white text-black" : "text-zinc-400 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.15)]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {damage.found && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="field-label">Damage Description</span>
            <input data-testid="admin-damage-desc" className="field" placeholder="e.g. Scuff to rear bumper" value={damage.description || ""} onChange={(e) => setDamage((d) => ({ ...d, description: e.target.value }))} />
          </label>
          <label className="block">
            <span className="field-label">Estimated Cost (£)</span>
            <input data-testid="admin-damage-cost" type="number" min="0" className="field" placeholder="Assessed per repair cost schedule" value={damage.est_cost || ""} onChange={(e) => setDamage((d) => ({ ...d, est_cost: e.target.value }))} />
          </label>
        </div>
      )}
      <p className="mt-3 text-[0.62rem] tracking-[0.12em] text-zinc-600 font-light">
        Charges depend on the actual damage and are assessed per the Repair Cost Schedule. The owner manually approves any deduction from the deposit.
      </p>

      <SectionHead>Notes</SectionHead>
      <textarea
        data-testid="admin-inspection-notes"
        className="field min-h-[90px] resize-y"
        placeholder="Inspection notes — handover condition, fuel, mileage, anything the renter should know…"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
      />

      <button data-testid="admin-save-inspection" className="admin-action primary mt-6" disabled={busy} onClick={save}>
        <Check size={13} /> {busy ? "Saving…" : "Save Inspection Record"}
      </button>
    </div>
  );
};

/* ---------------- AVAILABILITY ---------------- */

const monthMatrix = (offset) => {
  const base = new Date();
  const first = new Date(base.getFullYear(), base.getMonth() + offset, 1);
  const year = first.getFullYear();
  const month = first.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDay = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return { label: first.toLocaleDateString("en-GB", { month: "long", year: "numeric" }), cells };
};

const dateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const AvailabilityPanel = () => {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [calendar, setCalendar] = useState(null);
  const [block, setBlock] = useState({ start: "", end: "", reason: "" });
  const [pendingBlocks, setPendingBlocks] = useState(false);
  const [localRate, setLocalRate] = useState("");
  const [localRateNotice, setLocalRateNotice] = useState("");
  const [error, setError] = useState("");

  const loadCalendar = useCallback(async (vid) => {
    if (!vid) return;
    try {
      setCalendar(await apiFetch(`/admin/vehicles/${vid}/calendar`));
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => {
    apiFetch("/admin/vehicles").then((vs) => {
      setVehicles(vs);
      if (vs.length && !vehicleId) { setVehicleId(vs[0].id); loadCalendar(vs[0].id); }
    }).catch((e) => setError(e.message));
    apiFetch("/admin/settings").then((s) => {
      setPendingBlocks(!!s.pending_blocks_dates);
      setLocalRate(s.local_recovery_rate ? String(s.local_recovery_rate) : "");
    }).catch(() => {});
  }, []); // eslint-disable-line

  const dayState = (d) => {
    if (!calendar || !d) return "";
    const ds = dateStr(d);
    for (const bl of calendar.blocks) {
      if (ds >= bl.start_date && ds <= bl.end_date) return "blocked";
    }
    for (const b of calendar.bookings) {
      if (b.status !== "Pending" && ds >= b.start && ds <= b.end) return "booked";
    }
    for (const b of calendar.bookings) {
      if (b.status === "Pending" && ds >= b.start && ds <= b.end) return "pending";
    }
    return "";
  };

  const addBlock = async () => {
    if (!block.start || !block.end) { setError("Choose block start and end dates."); return; }
    setError("");
    try {
      await apiFetch(`/admin/vehicles/${vehicleId}/blocks`, { method: "POST", body: { start_date: block.start, end_date: block.end, reason: block.reason } });
      setBlock({ start: "", end: "", reason: "" });
      await loadCalendar(vehicleId);
    } catch (e) {
      setError(e.message);
    }
  };

  const removeBlock = async (id) => {
    try {
      await apiFetch(`/admin/blocks/${id}`, { method: "DELETE" });
      await loadCalendar(vehicleId);
    } catch (e) {
      setError(e.message);
    }
  };

  const togglePendingBlocks = async () => {
    const next = !pendingBlocks;
    setPendingBlocks(next);
    try {
      await apiFetch("/admin/settings", { method: "PUT", body: { pending_blocks_dates: next } });
    } catch {
      setPendingBlocks(!next);
    }
  };

  const saveLocalRate = async () => {
    setLocalRateNotice("");
    setError("");
    try {
      await apiFetch("/admin/settings", { method: "PUT", body: { pending_blocks_dates: pendingBlocks, local_recovery_rate: parseFloat(localRate) || 0 } });
      setLocalRateNotice("Saved — applies to new recovery requests immediately.");
      setTimeout(() => setLocalRateNotice(""), 4000);
    } catch (e) {
      setError(e.message);
    }
  };

  const today = dateStr(new Date());

  return (
    <div>
      <p className="text-sm text-zinc-400 font-light max-w-2xl leading-relaxed mb-8">
        Approved and active bookings automatically block the vehicle for overlapping dates. Manual blocks cover servicing, repairs and personal use. Pending bookings are shown but do not block dates unless enabled below.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10 items-start">
        <div>
          <span className="field-label">Vehicle</span>
          <select
            data-testid="admin-vehicle-select"
            className="field"
            value={vehicleId}
            onChange={(e) => { setVehicleId(e.target.value); loadCalendar(e.target.value); }}
          >
            {vehicles.map((v) => (
              <option key={v.id} value={v.id} className="bg-[#0b0b0d]">{v.name}</option>
            ))}
          </select>

          <div className="mt-8 p-5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] bg-white/[0.02]">
            <p className="text-[0.62rem] tracking-[0.3em] uppercase text-white mb-4">Manual Block</p>
            <div className="space-y-4">
              <label className="block"><span className="field-label">Start</span><input data-testid="admin-block-start" type="date" className="field" value={block.start} onChange={(e) => setBlock((b) => ({ ...b, start: e.target.value }))} /></label>
              <label className="block"><span className="field-label">End</span><input data-testid="admin-block-end" type="date" className="field" value={block.end} onChange={(e) => setBlock((b) => ({ ...b, end: e.target.value }))} /></label>
              <label className="block"><span className="field-label">Private Reason</span><input data-testid="admin-block-reason" className="field" placeholder="e.g. Servicing" value={block.reason} onChange={(e) => setBlock((b) => ({ ...b, reason: e.target.value }))} /></label>
              <button data-testid="admin-block-add" className="admin-action primary" onClick={addBlock}>Add Block</button>
            </div>
          </div>

          <button data-testid="admin-pending-blocks-toggle" onClick={togglePendingBlocks} className="mt-6 flex items-center gap-3 text-left">
            <span className={`chk ${pendingBlocks ? "on" : ""}`}>{pendingBlocks && <Check size={11} />}</span>
            <span className="text-[0.62rem] tracking-[0.18em] uppercase text-zinc-400">Pending bookings also reserve dates</span>
          </button>
          <p className="mt-2 text-[0.6rem] text-zinc-600 font-light">Owner option — off by default. Approved bookings and manual blocks always reserve dates.</p>

          <div className="mt-10 pt-8 border-t border-white/[0.08]" data-testid="admin-local-rate-setting">
            <span className="field-label">Peterborough Local Recovery Rate (£)</span>
            <p className="text-[0.6rem] text-zinc-600 font-light mb-3 leading-relaxed">
              Fixed price for recovery jobs where both the breakdown and destination are inside the Peterborough area (PE1–PE8). Applies instantly to new requests — no republish needed. Leave empty to show local jobs as "Quote Pending" until you enter the quote on the booking.
            </p>
            <div className="flex gap-3 max-w-xs">
              <input
                data-testid="admin-local-rate-input"
                type="number" min="0" step="0.01" className="field"
                placeholder="e.g. 70.00"
                value={localRate}
                onChange={(e) => setLocalRate(e.target.value)}
              />
              <button data-testid="admin-local-rate-save" className="admin-action primary md:!w-auto shrink-0" onClick={saveLocalRate}>Save</button>
            </div>
            {localRateNotice && <p className="text-xs text-emerald-300 font-light mt-2" data-testid="admin-local-rate-saved">{localRateNotice}</p>}
          </div>

          {calendar?.blocks?.length > 0 && (
            <div className="mt-8">
              <p className="text-[0.62rem] tracking-[0.3em] uppercase text-white mb-3">Active Blocks</p>
              {calendar.blocks.map((bl) => (
                <div key={bl.id} className="flex items-center justify-between py-2.5 border-b border-white/[0.06]">
                  <span className="text-xs text-zinc-400 font-light">{bl.start_date} → {bl.end_date}{bl.reason ? ` · ${bl.reason}` : ""}</span>
                  <button data-testid={`admin-block-del-${bl.id}`} onClick={() => removeBlock(bl.id)} className="text-zinc-600 hover:text-red-300 transition-colors p-2"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          {error && <p className="text-xs text-red-300 mb-4">{error}</p>}
          <div className="flex flex-wrap gap-5 mb-6 text-[0.6rem] tracking-[0.2em] uppercase text-zinc-500">
            <span className="flex items-center gap-2"><span className="w-3 h-3 bg-emerald-300/20 inline-block" /> Approved / Active</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 bg-amber-200/20 inline-block" /> Pending</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 bg-red-300/15 inline-block" /> Manual Block</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[0, 1].map((offset) => {
              const { label, cells } = monthMatrix(offset);
              return (
                <div key={offset} className="shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)] p-5">
                  <p className="text-[0.65rem] tracking-[0.3em] uppercase text-white mb-4">{label}</p>
                  <div className="grid grid-cols-7 gap-px mb-1">
                    {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
                      <span key={i} className="text-center text-[0.55rem] tracking-[0.2em] text-zinc-600 py-1">{d}</span>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-px">
                    {cells.map((d, i) =>
                      d ? (
                        <span key={i} className={`cal-day ${dayState(d)} ${dateStr(d) === today ? "today" : ""}`} title={dayState(d)}>
                          {d.getDate()}
                        </span>
                      ) : (
                        <span key={i} className="cal-day calday-spacer" />
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {calendar?.bookings?.length > 0 && (
            <div className="mt-8">
              <p className="text-[0.62rem] tracking-[0.3em] uppercase text-white mb-3">Bookings — {calendar.vehicle?.name}</p>
              {calendar.bookings.map((b) => (
                <div key={b.ref} className="flex items-center justify-between py-2.5 border-b border-white/[0.06]">
                  <span className="text-xs text-zinc-400 font-light">{b.ref} · {b.customer} · {b.start} → {b.end}</span>
                  <span className={`status-pill border ${STATUS_STYLE[b.status]}`}>{b.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------------- VEHICLE ENQUIRIES ---------------- */

const EnquiriesPanel = ({ type }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    apiFetch(`/admin/enquiries?type=${type}`).then(setItems).catch((e) => setError(e.message));
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await apiFetch(`/admin/enquiries/${id}`, { method: "PUT", body: { status } });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <div>
      <p className="text-sm text-zinc-400 font-light mb-8 max-w-2xl leading-relaxed">
        Enquiries for the enquiry-only fleet vehicles (Audi RSQ8, Range Rover Sport, Range Rover Sport SVR, Lamborghini Urus). Pricing is arranged directly — never through online checkout.
      </p>
      {error && <p className="text-xs text-red-300 mb-4">{error}</p>}
      <div className="divide-y divide-white/[0.06] border-y border-white/[0.08]">
        {items.map((e) => (
          <div key={e.id} data-testid={`admin-enquiry-${e.id}`} className="px-5 py-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-sm text-white font-light">{e.name}{e.vehicle_name ? ` — ${e.vehicle_name}` : ""}</p>
              <p className="mt-1.5 text-[0.62rem] tracking-[0.15em] text-zinc-500 font-light">
                {e.phone}{e.email ? ` · ${e.email}` : ""}{e.preferred_dates ? ` · ${e.preferred_dates}` : ""} · {new Date(e.created_at).toLocaleString("en-GB")}
              </p>
              {e.message && <p className="mt-2 text-sm text-zinc-400 font-light">{e.message}</p>}
            </div>
            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              <a className="admin-action md:!w-auto" href={waLinkAdmin(e.phone)} target="_blank" rel="noopener noreferrer"><MessageCircle size={13} /> WhatsApp</a>
              <select
                data-testid={`admin-enquiry-status-${e.id}`}
                className="field w-full md:!w-36 !py-2"
                value={e.status}
                onChange={(ev) => setStatus(e.id, ev.target.value)}
              >
                {["New", "In Progress", "Closed"].map((s) => <option key={s} className="bg-[#0b0b0d]">{s}</option>)}
              </select>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="px-5 py-10 text-sm text-zinc-500 font-light">No enquiries yet.</p>}
      </div>
    </div>
  );
};

/* ---------------- RECOVERY BOOKINGS ---------------- */

const RecoveryBookingsPanel = () => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const data = await apiFetch("/admin/recovery-bookings");
      setItems(data);
      if (selected) {
        const fresh = data.find((r) => r.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (e) {
      setError(e.message);
    }
  }, [selected]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div>
      <p className="text-sm text-zinc-400 font-light mb-8 max-w-2xl leading-relaxed">
        Recovery requests submitted through the Senna Recovery page. Review mileage, pricing and timing, then accept once a truck and driver are confirmed.
      </p>
      {error && <p className="text-xs text-red-300 mb-4">{error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-10 items-start">
        <div>
          <div className="hidden md:block divide-y divide-white/[0.06] border-y border-white/[0.08]">
            {items.map((r) => (
              <button
                key={r.id}
                data-testid={`admin-recovery-row-${r.ref}`}
                onClick={() => setSelected(r)}
                className={`admin-table-row w-full text-left px-5 py-5 flex items-center justify-between gap-4 ${selected?.id === r.id ? "selected" : ""}`}
              >
                <div>
                  <p className="text-sm text-white font-light">{r.name}</p>
                  <p className="mt-1.5 text-[0.62rem] tracking-[0.2em] uppercase text-zinc-500">{r.ref} · {r.vehicle_make} {r.vehicle_model} · {r.recovery_date} {r.recovery_time}</p>
                </div>
                <span className={`status-pill border ${RECOVERY_STATUS_STYLE[r.status]}`}>{r.status}</span>
              </button>
            ))}
            {items.length === 0 && <p className="px-5 py-10 text-sm text-zinc-500 font-light">No recovery bookings yet.</p>}
          </div>

          <div className="md:hidden space-y-4" data-testid="admin-recovery-cards">
            {items.map((r) => (
              <button key={r.id} data-testid={`admin-recovery-card-${r.ref}`} onClick={() => setSelected(r)} className="m-card w-full text-left p-5">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-display text-lg text-white leading-snug">{r.name}</p>
                  <span className={`status-pill border shrink-0 ${RECOVERY_STATUS_STYLE[r.status]}`}>{r.status}</span>
                </div>
                <p className="mt-3 text-sm text-zinc-300 font-light">{r.vehicle_make} {r.vehicle_model} · {r.vehicle_reg}</p>
                <p className="mt-1 text-sm text-zinc-500 font-light">{r.breakdown_postcode} → {r.destination_postcode}</p>
                <p className="mt-3 text-[0.6rem] tracking-[0.25em] uppercase text-zinc-600">{r.ref} · {r.recovery_date} at {r.recovery_time}</p>
              </button>
            ))}
            {items.length === 0 && <p className="py-10 text-center text-sm text-zinc-500 font-light">No recovery bookings yet.</p>}
          </div>
        </div>

        {selected && (
          <div className="max-md:fixed max-md:inset-0 max-md:z-50 max-md:overflow-y-auto max-md:bg-[#050506] max-md:px-4 max-md:py-6">
            <button data-testid="admin-recovery-close" onClick={() => setSelected(null)} className="md:hidden mb-5 flex items-center gap-2 text-[0.62rem] tracking-[0.3em] uppercase text-zinc-400">
              <ArrowLeft size={14} /> All Recoveries
            </button>
            <RecoveryDetail key={selected.id + selected.status} r={selected} refresh={load} />
          </div>
        )}
        {!selected && (
          <div className="hidden lg:flex items-center justify-center min-h-[300px] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)] text-zinc-600 text-sm font-light">
            Select a recovery booking to review it.
          </div>
        )}
      </div>
    </div>
  );
};

const RecoveryDetail = ({ r, refresh }) => {
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [quoteInput, setQuoteInput] = useState("");

  const viewRecoveryPhoto = async (rid, idx) => {
    const url = await fetchFileBlobUrl(`/admin/recovery-bookings/${rid}/photos/${idx}`);
    window.open(url, "_blank");
  };

  const transition = async (action) => {
    setBusy(true); setError(""); setNotice("");
    try {
      await apiFetch(`/admin/recovery-bookings/${r.id}/transition`, { method: "POST", body: { action, final_total: parseFloat(quoteInput) || 0 } });
      setQuoteInput("");
      await refresh();
      setNotice("Updated.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1)] bg-[#08080a] p-5 md:p-9" data-testid="admin-recovery-detail">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-[0.6rem] tracking-[0.3em] uppercase text-zinc-500">{r.ref} · submitted {new Date(r.created_at).toLocaleString("en-GB")}</p>
          <h2 className="font-display text-2xl text-white mt-2">{r.vehicle_make} {r.vehicle_model} · {r.vehicle_reg}</h2>
        </div>
        <span className={`status-pill border ${RECOVERY_STATUS_STYLE[r.status]}`} data-testid="admin-recovery-status">{r.status}</span>
      </div>

      <SectionHead>Customer</SectionHead>
      <Row label="Name" value={r.name} />
      <Row label="Mobile" value={r.phone} />
      <Row label="Email" value={r.email || "—"} />

      <SectionHead>Recovery</SectionHead>
      <Row label="Breakdown Location" value={r.breakdown_postcode} />
      <Row label="Destination" value={r.destination_postcode} />
      <Row label="Vehicle" value={`${r.vehicle_make} ${r.vehicle_model} · ${r.vehicle_reg}`} />
      <Row label="Condition" value={r.condition} />
      <Row label="Requested" value={`${r.recovery_date} at ${r.recovery_time}`} />
      <SectionHead>Incident, Safety & Vehicle Condition</SectionHead>
      <Row label="What Happened" value={r.description || "—"} />
      <SafetyRow label="Customer / Passengers Safe" value={r.passengers_safe} danger={r.passengers_safe === "No"} />
      <SafetyRow label="Vehicle In Safe Position" value={r.vehicle_safe} danger={r.vehicle_safe === "No"} />
      <Row label="Goes Into Neutral" value={r.neutral || "—"} />
      <Row label="Vehicle Turns Over" value={r.turns_over || "—"} />
      <SafetyRow label="Vehicle Smoking" value={r.smoking} danger={r.smoking === "Yes"} />
      {(r.photos || []).length > 0 && (
        <div className="mt-4">
          <p className="text-[0.62rem] tracking-[0.22em] uppercase text-zinc-500 mb-3">Photos ({r.photos.length})</p>
          <div className="flex flex-wrap gap-3">
            {r.photos.map((p, i) => (
              <button key={i} data-testid={`admin-recovery-photo-${i}`} onClick={() => viewRecoveryPhoto(r.id, i)} className="veh-thumb-btn">
                <span className="block px-4 py-3 text-[0.6rem] tracking-[0.2em] uppercase text-zinc-300">{p.filename || `Photo ${i + 1}`}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <SectionHead>Recovery Pricing</SectionHead>
      <Row label="Pricing Method" value={r.pricing_type === "local" ? "LOCAL PETERBOROUGH — FIXED RATE" : "OUT OF TOWN — MILEAGE RATE"} testid="admin-recovery-pricing" />
      {r.pricing_type === "local" ? (
        <Row label="Fixed Local Price" value={r.estimated_total ? gbp(r.estimated_total) : "Quote pending — enter below before accepting"} testid="admin-recovery-price" />
      ) : (
        <>
          <Row label="Truck Travel (PE4 6BX → breakdown)" value={`${r.truck_miles} miles`} />
          <Row label="Recovery Journey (breakdown → destination)" value={`${r.recovery_miles} miles`} />
          <Row label="Total Billable Mileage" value={`${r.total_miles} miles`} />
          <Row label="Rate" value={`${r.band_label} · £${r.rate.toFixed(2)}/mile`} />
          <Row label="Estimated Recovery Price" value={gbp(r.estimated_total)} testid="admin-recovery-price" />
        </>
      )}

      <SectionHead>Payment</SectionHead>
      {r.payment ? (
        <div data-testid="admin-recovery-payment">
          <div className="mb-4">
            <span className={`status-pill border ${RECOVERY_PAYMENT_STYLE[r.payment.status] || ""}`} data-testid="admin-recovery-payment-status">
              {RECOVERY_PAYMENT_LABELS[r.payment.status] || r.payment.status}
            </span>
          </div>
          <Row label="Total Recovery Quote" value={gbp(r.estimated_total)} />
          {["klarna", "afterpay_clearpay"].includes(r.payment.method) ? (
            <>
              <Row label="Payment Method" value={`${METHOD_LABELS[r.payment.method]} — full quote authorisation`} testid="admin-recovery-method" />
              <Row label="Full Quote Authorisation" value={`${gbp(r.estimated_total)} · ${r.payment.bnpl_status || "pending"}`} testid="admin-recovery-bnpl" />
              <Row label="Final Capture" value={r.payment.capture_status} />
            </>
          ) : (
            <>
              <Row label="Payment Method" value="Card — 30% deposit + 70% balance" testid="admin-recovery-method" />
              <Row label="30% Deposit" value={`${gbp(r.payment.deposit_amount)} · ${r.payment.deposit_status}`} testid="admin-recovery-deposit" />
              <Row label="Remaining 70% Balance" value={`${gbp(r.payment.balance_amount)} · ${r.payment.balance_status}`} testid="admin-recovery-balance" />
              <Row label="Final Capture" value={r.payment.capture_status} />
            </>
          )}
          {["awaiting_payment", "deposit_paid"].includes(r.payment.status) && (
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                data-testid="admin-copy-paylink"
                className="admin-action md:!w-auto"
                onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/recovery/pay/${r.payment.token}`); setNotice("Payment link copied."); }}
              >
                Copy Payment Link
              </button>
              <a
                data-testid="admin-whatsapp-paylink"
                className="admin-action md:!w-auto"
                href={`${waLinkAdmin(r.phone)}?text=${encodeURIComponent(`Senna Recovery ${r.ref} — your recovery is confirmed. Please complete secure payment here: ${window.location.origin}/recovery/pay/${r.payment.token}`)}`}
                target="_blank" rel="noopener noreferrer"
              >
                <MessageCircle size={13} /> WhatsApp Payment Link
              </a>
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-zinc-500 font-light">Payment link is generated when you accept the recovery.</p>
      )}

      <SectionHead>Actions</SectionHead>
      {r.status === "Pending" && r.pricing_type === "local" && !r.estimated_total && (
        <div className="mb-4 max-w-xs" data-testid="admin-local-quote-box">
          <span className="field-label">Local Recovery Quote (£) — required before accepting</span>
          <input
            data-testid="admin-local-quote-input"
            type="number" min="1" step="0.01" className="field"
            placeholder="Enter the fixed local price"
            value={quoteInput}
            onChange={(e) => setQuoteInput(e.target.value)}
          />
        </div>
      )}
      <div className="flex flex-wrap gap-3 mt-2" data-testid="admin-recovery-actions">
        {r.status === "Pending" && (
          <>
            <button data-testid="admin-recovery-accept" className="admin-action primary" disabled={busy} onClick={() => transition("accept")}>
              <Check size={13} /> Accept Recovery
            </button>
            <button data-testid="admin-recovery-unavailable" className="admin-action danger" disabled={busy} onClick={() => transition("unavailable")}>
              <X size={13} /> Unavailable / Reject
            </button>
          </>
        )}
        {r.status === "Accepted" && (
          <>
            {r.payment && !["balance_authorised", "fully_paid"].includes(r.payment.status) && (
              <p className="w-full text-xs text-amber-200/80 font-light">
                {["klarna", "afterpay_clearpay"].includes(r.payment.method)
                  ? `The full ${METHOD_LABELS[r.payment.method]} authorisation must succeed before the job can proceed.`
                  : "Deposit and 70% balance authorisation must succeed before the job can proceed."}
              </p>
            )}
            <button data-testid="admin-recovery-inprogress" className="admin-action" disabled={busy} onClick={() => transition("in_progress")}>Mark In Progress</button>
            <button data-testid="admin-recovery-complete" className="admin-action" disabled={busy} onClick={() => transition("complete")}>
              <Check size={13} /> Mark Completed
            </button>
          </>
        )}
        {r.status === "In Progress" && (
          <button data-testid="admin-recovery-complete" className="admin-action" disabled={busy} onClick={() => transition("complete")}>
            <Check size={13} /> Mark Completed
          </button>
        )}
        <a data-testid="admin-recovery-call" className="admin-action" href={telLink(r.phone)}>
          <Phone size={13} /> Call Customer
        </a>
        <a data-testid="admin-recovery-whatsapp" className="admin-action" href={waLinkAdmin(r.phone)} target="_blank" rel="noopener noreferrer">
          <MessageCircle size={13} /> WhatsApp Customer
        </a>
      </div>

      <SectionHead>History</SectionHead>
      <div className="space-y-2">
        {[...(r.history || [])].reverse().map((h, i) => (
          <p key={i} className="text-xs text-zinc-500 font-light">
            <span className="text-zinc-300">{h.action}</span> · {new Date(h.at).toLocaleString("en-GB")} · {h.by}
          </p>
        ))}
      </div>

      {notice && <p data-testid="admin-recovery-notice" className="mt-6 text-xs text-emerald-300/90 font-light">{notice}</p>}
      {error && <p data-testid="admin-recovery-error" className="mt-6 text-xs text-red-300 font-light">{error}</p>}
    </div>
  );
};

/* ---------------- SYSTEM ---------------- */

const SystemPanel = () => {
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    apiFetch("/admin/settings").then(setSettings).catch(() => {});
    apiFetch("/admin/email-logs").then(setLogs).catch(() => {});
  }, []);

  if (!settings) return null;

  const integrations = [
    ["Card payments (Stripe)", settings.payments_enabled, "Authorise on submission, capture on approval, release on rejection, deposit refunds"],
    ["Email notifications (IONOS SMTP)", settings.email_enabled, "Customer + owner booking notifications from bookings@sennaprestige.co.uk"],
    ["Live road-distance pricing", settings.distance_enabled, "Automatic postcode mileage from/to PE4 6BX"],
    ["Private document storage", settings.storage_enabled, "Licence, proof of address and inspection photos"],
  ];

  return (
    <div className="max-w-3xl">
      <p className="text-[0.62rem] tracking-[0.32em] uppercase text-white mb-4">Integrations</p>
      <div className="divide-y divide-white/[0.06] border-y border-white/[0.08]">
        {integrations.map(([label, on, desc]) => (
          <div key={label} className="py-4 flex items-center justify-between gap-6">
            <div>
              <p className="text-sm text-zinc-200 font-light">{label}</p>
              <p className="text-xs text-zinc-500 font-light mt-1">{desc}</p>
            </div>
            <span data-testid={`admin-integration-${label.split(" ")[0].toLowerCase()}`} className={`status-pill border ${on ? "text-emerald-300 border-emerald-300/30" : "text-zinc-400 border-zinc-500/30"}`}>
              {on ? "Active" : "Awaiting Setup"}
            </span>
          </div>
        ))}
      </div>

      <p className="text-[0.62rem] tracking-[0.32em] uppercase text-white mt-10 mb-4">Change Password</p>
      <ChangePassword />

      <p className="text-[0.62rem] tracking-[0.32em] uppercase text-white mt-10 mb-4">Recent Email Log</p>
      <div className="divide-y divide-white/[0.06] border-y border-white/[0.08] max-h-72 overflow-y-auto">
        {logs.map((l, i) => (
          <div key={i} className="py-3 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs text-zinc-300 font-light">{l.subject}</p>
              <p className="text-[0.6rem] text-zinc-600 font-light mt-1">{l.to || "no recipient"} · {new Date(l.created_at).toLocaleString("en-GB")}</p>
            </div>
            <span className={`text-[0.58rem] tracking-[0.15em] uppercase ${l.status === "sent" ? "text-emerald-300" : "text-zinc-500"}`}>{l.status}</span>
          </div>
        ))}
        {logs.length === 0 && <p className="py-6 text-sm text-zinc-500 font-light">No email events yet.</p>}
      </div>
    </div>
  );
};

const ChangePassword = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      await apiFetch("/auth/change-password", { method: "POST", body: { current_password: current, new_password: next } });
      setMsg("Password updated. Use it next time you sign in.");
      setCurrent(""); setNext("");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-sm space-y-4" data-testid="admin-change-password">
      <label className="block">
        <span className="field-label">Current Password</span>
        <input data-testid="admin-cpw-current" type="password" className="field" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
      </label>
      <label className="block">
        <span className="field-label">New Password</span>
        <input data-testid="admin-cpw-new" type="password" className="field" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" />
      </label>
      {msg && <p data-testid="admin-cpw-success" className="text-xs text-emerald-300/90 font-light">{msg}</p>}
      {err && <p data-testid="admin-cpw-error" className="text-xs text-red-300 font-light">{err}</p>}
      <button data-testid="admin-cpw-submit" className="admin-action primary" disabled={busy || !current || !next} onClick={submit}>
        {busy ? "Updating…" : "Update Password"}
      </button>
    </div>
  );
};
